import { useMemo, type ComponentType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Donut,
  Grid,
  Metric,
  Page,
  Stack,
  cx,
  type IconProps,
  type Tone,
  AssetIcon,
  CampaignIcon,
  ConnectorIcon,
  CreativeIcon,
  HomeIcon,
  ReviewIcon,
} from '@integra/ui';
import {
  useAssets,
  useCampaigns,
  useChannelSpecHealth,
  useConnectorConfigs,
  useConnectorHealth,
  useUsage,
  useWorkspaces,
} from '../api/hooks.js';

/**
 * The Command Centre (§20.5, estate baseline "guided home"). The landing surface
 * is not a table — it greets, states what the product is doing, and points at the
 * one thing that needs a human right now.
 *
 * Everything here is REAL workspace state: approvals waiting, channels that need
 * attention, spec drift, and the week's METERED activity. Nothing is invented, and
 * every figure keeps its provenance (§2.7) — the "This week" tiles are measured
 * counts from INTEGRA's own metering, never blended estimates.
 */

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** The distribution loop, in order — the spine the whole product runs on. */
const LOOP: readonly { step: string; stage: string; to: string; blurb: string }[] = [
  { step: '1', stage: 'Plan', to: 'campaigns', blurb: 'Sequence the drop' },
  { step: '2', stage: 'Create', to: 'assets', blurb: 'One hero source' },
  { step: '3', stage: 'Distribute', to: 'connectors', blurb: 'Fit every channel' },
  { step: '4', stage: 'Measure', to: 'weekly-review', blurb: 'What actually landed' },
  { step: '5', stage: 'Optimise', to: 'optimise', blurb: 'Feed it back' },
];

type Severity = 'ok' | 'warn' | 'bad';
const SEVERITY_STRIPE: Record<Severity, string> = {
  ok: 'bg-state-success',
  warn: 'bg-state-warning',
  bad: 'bg-state-error',
};

interface NeedRow {
  readonly id: string;
  readonly severity: Severity;
  readonly title: string;
  readonly detail: string;
  readonly action: string;
  readonly to: string;
  readonly primary?: boolean;
}

export function HomePage() {
  const { workspaceId = '' } = useParams();
  const navigate = useNavigate();
  const go = (to: string): void => navigate(`/w/${workspaceId}/${to}`);

  const { data: workspaces } = useWorkspaces();
  const workspace = workspaces?.data.find((w) => w.id === workspaceId);

  const campaigns = useCampaigns(workspaceId);
  const assets = useAssets(workspaceId);
  const health = useConnectorHealth(workspaceId);
  const configs = useConnectorConfigs(workspaceId);
  const usage = useUsage(workspaceId);
  const specHealth = useChannelSpecHealth(workspaceId);

  const campaignList = campaigns.data?.data ?? [];
  const healthEntries = health.data?.data ?? [];
  const usageRows = usage.data?.usage ?? [];

  const connectorCount = configs.data?.data.length ?? 0;
  const assetCount = assets.data?.data.length ?? 0;
  const healthyCount = healthEntries.filter((h) => h.healthy).length;
  const unhealthy = healthEntries.filter((h) => !h.healthy);
  const awaitingApproval = campaignList.filter((c) => c.state === 'awaiting_approval');

  // --- Needs you: only real, actionable signals, most urgent first. ---
  const needs = useMemo<NeedRow[]>(() => {
    const rows: NeedRow[] = [];
    if (awaitingApproval.length > 0) {
      rows.push({
        id: 'approvals',
        severity: 'warn',
        title: `${String(awaitingApproval.length)} ${awaitingApproval.length === 1 ? 'post is' : 'posts are'} waiting on approval`,
        detail: awaitingApproval
          .slice(0, 2)
          .map((c) => c.name)
          .join(', '),
        action: 'Review',
        to: 'campaigns',
        primary: true,
      });
    }
    if (unhealthy.length > 0) {
      rows.push({
        id: 'channels',
        severity: 'bad',
        title: `${String(unhealthy.length)} ${unhealthy.length === 1 ? 'channel needs' : 'channels need'} attention`,
        detail: unhealthy
          .slice(0, 2)
          .map((h) => `${h.displayName}${h.detail !== null ? ` — ${h.detail}` : ''}`)
          .join(' · '),
        action: 'Fix',
        to: 'connectors',
      });
    }
    const report = specHealth.data;
    if (report !== undefined) {
      const drift = report.drift;
      const stale = report.stale;
      rows.push({
        id: 'spec',
        severity: drift > 0 ? 'bad' : stale > 0 ? 'warn' : 'ok',
        title:
          drift > 0
            ? `${String(drift)} channel ${drift === 1 ? 'spec has' : 'specs have'} drifted`
            : stale > 0
              ? `${String(stale)} channel ${stale === 1 ? 'spec is' : 'specs are'} due review`
              : `Spec health: ${String(report.ok)} in sync`,
        detail:
          drift > 0 || stale > 0
            ? 'A pinned spec no longer matches its channel — content could mis-map.'
            : `0 drifted · 0 due review · checked against every connector`,
        action: 'View',
        to: 'channel-standards',
      });
    }
    return rows;
  }, [awaitingApproval, unhealthy, specHealth.data]);

  const allClear = needs.every((n) => n.severity === 'ok');

  // Setup progress lives in the sidebar nudge — one home for it, not three.

  const hour = new Date().getHours();
  const usageWindow = usage.data?.window;

  return (
    <Page
      title={greeting(hour)}
      icon={HomeIcon}
      description={
        connectorCount > 0
          ? `${workspace?.name ?? 'This workspace'} · ${String(connectorCount)} ${connectorCount === 1 ? 'channel' : 'channels'} connected. Publish once — INTEGRA fits it to each channel's gold standard and blocks what can't land.`
          : `${workspace?.name ?? 'This workspace'} · publish once, land right on every channel, never mis-mapped.`
      }
      actions={<Button onClick={() => go('assets')}>✦ Publish</Button>}
    >
      <Stack gap="lg">
        {/* The loop — the product's spine, and a way to jump into any stage. */}
        <div className="flex flex-wrap gap-1 rounded-lg bg-navy-700 p-1.5">
          {LOOP.map((s, i) => (
            <button
              key={s.stage}
              type="button"
              onClick={() => go(s.to)}
              className="group relative flex-1 basis-40 rounded-md px-3 py-2 text-left transition-colors duration-fast hover:bg-navy-600"
            >
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -left-1 top-1/2 -translate-y-1/2 text-gold-400"
                >
                  →
                </span>
              )}
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gold-400">
                {s.step} {s.stage}
              </span>
              <span className="mt-0.5 block text-xs text-navy-200 group-hover:text-ink-inverse">
                {s.blurb}
              </span>
            </button>
          ))}
        </div>

        {/* Assurance — the governance moat, made visible on the command centre. */}
        <button
          type="button"
          onClick={() => go('channel-standards')}
          className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-navy-600 bg-navy-800 px-4 py-2.5 text-left transition-colors duration-fast hover:border-gold-400"
        >
          <span className="text-[11px] font-semibold uppercase tracking-widest text-gold-400">
            Governed by design
          </span>
          <span className="text-sm text-navy-200">
            Every publish passes an approval gate · every run is versioned and audited · measured
            and estimated are never blended.
          </span>
        </button>

        {/* This week — measured KPIs, full-width and confident. */}
        <section>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            This week
            {usageWindow !== undefined
              ? ` · metered ${fmtDate(usageWindow.from)}–${fmtDate(usageWindow.to)}`
              : ''}
          </p>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="flex items-center justify-center rounded-md border border-edge bg-surface px-4 py-3">
              {healthEntries.length > 0 ? (
                <Donut
                  value={healthyCount}
                  total={healthEntries.length}
                  caption="Channels live"
                  series={healthyCount === healthEntries.length ? 'success' : 'accent'}
                />
              ) : (
                <Metric label="Channels live" value="0" caption="Connect a channel to begin." />
              )}
            </div>
            <Metric label="Assets" value={assetCount} provenance="measured" />
            {usageRows.slice(0, 2).map((u) => (
              <Metric
                key={u.metric}
                label={titleiseMetric(u.metric)}
                value={u.quantity.toLocaleString()}
                provenance="measured"
              />
            ))}
          </div>
        </section>

        {/* Needs you — the one focal list, full width. */}
        <Card
          title="Needs you"
          subtitle="The only list that matters — what's blocking a post from landing right."
        >
          {needs.length === 0 ? (
            <p className="py-4 text-center text-sm text-ink-muted">
              Connect a channel to start — there's nothing to action yet.
            </p>
          ) : allClear ? (
            <Stack gap="sm">
              <p className="text-sm text-navy-700">
                <span className="font-semibold text-state-success">All clear.</span> No approvals
                pending, every channel healthy, and specs in sync.
              </p>
              {needs.map((n) => (
                <NeedItem key={n.id} need={n} onClick={() => go(n.to)} />
              ))}
            </Stack>
          ) : (
            <Stack gap="sm">
              {[...needs]
                .sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
                .map((n) => (
                  <NeedItem key={n.id} need={n} onClick={() => go(n.to)} />
                ))}
            </Stack>
          )}
        </Card>

        {/* Quick actions — one click into each stage of the loop */}
        <Card title="Quick actions" subtitle="Start anywhere in the loop.">
          <Grid min="sm" gap="sm">
            <QuickAction
              Icon={CampaignIcon}
              label="New campaign"
              hint="Visual builder"
              onClick={() => go('campaigns/new')}
            />
            <QuickAction
              Icon={AssetIcon}
              label="Publish an asset"
              hint="To every channel"
              onClick={() => go('assets')}
            />
            <QuickAction
              Icon={ConnectorIcon}
              label="Add a channel"
              hint="Connect a destination"
              onClick={() => go('connectors')}
            />
            <QuickAction
              Icon={CreativeIcon}
              label="Creative studio"
              hint="Brand, templates"
              onClick={() => go('creative')}
            />
            <QuickAction
              Icon={ReviewIcon}
              label="Weekly review"
              hint="Measured winners"
              onClick={() => go('weekly-review')}
            />
          </Grid>
        </Card>
      </Stack>
    </Page>
  );
}

function severityRank(s: Severity): number {
  return s === 'bad' ? 2 : s === 'warn' ? 1 : 0;
}

const SEVERITY_ACTION_TONE: Record<Severity, Tone> = {
  ok: 'neutral',
  warn: 'warning',
  bad: 'error',
};

function NeedItem({ need, onClick }: { need: NeedRow; onClick: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-edge bg-surface px-3 py-2.5">
      <span
        className={cx('h-9 w-1 shrink-0 rounded-full', SEVERITY_STRIPE[need.severity])}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-navy-700">{need.title}</p>
        {need.detail !== '' && <p className="truncate text-xs text-ink-muted">{need.detail}</p>}
      </div>
      <Button variant={need.primary === true ? 'primary' : 'secondary'} size="sm" onClick={onClick}>
        {need.action}
      </Button>
      <Badge tone={SEVERITY_ACTION_TONE[need.severity]} dot className="hidden sm:inline-flex">
        {need.severity === 'bad' ? 'Blocking' : need.severity === 'warn' ? 'Waiting' : 'Healthy'}
      </Badge>
    </div>
  );
}

function QuickAction({
  Icon,
  label,
  hint,
  onClick,
}: {
  Icon: ComponentType<IconProps>;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-3 rounded-md border border-edge bg-surface px-3 py-3 text-left transition-colors duration-fast hover:border-navy-400 hover:bg-surface-sunken"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-navy-700 text-gold-400">
        <Icon size={18} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-navy-700">{label}</span>
        <span className="block text-xs text-ink-muted">{hint}</span>
      </span>
    </button>
  );
}

function titleiseMetric(metric: string): string {
  const words = metric.replace(/[_-]/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
